// src/pages/subscription/EntitlementDetailPage.tsx
// Read-only view of one entitlement (entitlements are projections of
// plans/add-ons — there is nothing to edit). The one action is re-running
// the access check, via EntitlementCheckDialog prefilled with this
// entitlement's tenant, feature key, and product.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useSubscriptionEntitlement, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import { EntitlementCheckDialog } from '@/features/subscription/EntitlementCheckDialog';
import { formatDate, humanizeEnum } from '@/features/subscription/format';

export const EntitlementDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useSubscriptionEntitlement();
  const { t } = useI18n();
  const [showCheck, setShowCheck] = useState(false);
  const item = store.currentItem as any;

  useEffect(() => {
    if (id) store.GetEntitlement(id);
    return () => { store.clearCurrent(); };
  }, [id]);

  if (store.isLoadingItem) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }
  if (store.error) {
    return <><ErrorAlert error={store.error} /><Link to="/subscriptions/entitlements" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></>;
  }
  if (!item) {
    return <Alert variant="warning" title={t('common.notFound')}><Link to="/subscriptions/entitlements" className="mt-2 inline-block text-sm underline text-primary">{t('common.backToList')}</Link></Alert>;
  }

  const limit = item.limit_value ?? 0;
  const usage = item.current_usage ?? 0;
  const percentage = limit > 0 ? Math.min((usage / limit) * 100, 100) : 0;

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">Subscriptions</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/subscriptions/entitlements" className="text-muted-foreground hover:text-foreground">Entitlements</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{String(item.feature_name || item.feature_key || id)}</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{String(item.feature_name || item.feature_key || '')}</h1>
            {item.is_enabled !== false
              ? <Badge variant="success" size="sm">Enabled</Badge>
              : <Badge variant="secondary" size="sm">Disabled</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-1 font-mono">{String(item.feature_key ?? '')}</p>
        </div>
        <Button variant="secondary" onClick={() => setShowCheck(true)}>{t('subscription.entitlement.check')}</Button>
      </div>

      <EntitlementCheckDialog
        open={showCheck}
        onClose={() => setShowCheck(false)}
        initial={{
          tenantId: item.tenant?.id ?? '',
          featureKey: item.feature_key ?? '',
          productId: item.product?.id ?? '',
        }}
      />

      {/* Feature */}
      <Card>
        <CardHeader><CardTitle>Feature</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Feature Key</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.feature_key ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Feature Name</dt>
              <dd className="text-sm text-foreground col-span-2">{String(item.feature_name || '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Type</dt>
              <dd className="text-sm col-span-2">
                <Badge variant="secondary" size="sm">{humanizeEnum(item.type, 'ENTITLEMENT_TYPE_') || '--'}</Badge>
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Access & Usage */}
      <Card>
        <CardHeader><CardTitle>Access &amp; Usage</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Enabled</dt>
              <dd className="text-sm col-span-2">
                {item.is_enabled !== false
                  ? <Badge variant="success" size="sm">Enabled</Badge>
                  : <Badge variant="secondary" size="sm">Disabled</Badge>}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Limit / Usage</dt>
              <dd className="text-sm text-foreground col-span-2">
                {limit > 0 ? (
                  <div className="flex items-center gap-3 max-w-[280px]">
                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full ${percentage >= 90 ? 'bg-destructive' : percentage >= 70 ? 'bg-warning' : 'bg-primary'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">{usage} / {limit}</span>
                  </div>
                ) : (
                  <span className="tabular-nums">{usage} / --</span>
                )}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Reset Period</dt>
              <dd className="text-sm text-foreground col-span-2">{humanizeEnum(item.reset_period, 'USAGE_LIMIT_PERIOD_') || '--'}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Expires At</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.expires_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Source */}
      <Card>
        <CardHeader><CardTitle>Source</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Source Plan</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.source_plan_slug || '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Source Add-On</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.source_addon_id || '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Subscription</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">
                {item.subscription?.id
                  ? <Link to={`/subscriptions/${item.subscription.id}`} className="text-primary hover:underline">{item.subscription.id}</Link>
                  : '--'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Tenant</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.tenant?.id ?? '--')}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Product</dt>
              <dd className="text-sm text-foreground col-span-2 font-mono">{String(item.product?.id ?? '--')}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Timestamps */}
      <Card>
        <CardHeader><CardTitle>Timestamps</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.created_at)}</dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Updated</dt>
              <dd className="text-sm text-foreground col-span-2">{formatDate(item.updated_at)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default EntitlementDetailPage;
